import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_TONES = {
  "New Lead": "bg-sand text-mutedink",
  "Intake Received": "bg-teal-light text-teal",
  "RFE / Action Needed": "bg-red-950/60 text-red-300",
  "Ready to File": "bg-sage-light text-sage",
  "Approved / Completed": "bg-sage-light text-sage",
  Closed: "bg-sand text-mutedink",
};

export default function StaffCases() {
  const [cases, setCases] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [clients, setClients] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("cases");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", service_type: "" });

  const load = () => {
    api.get("/cases").then((r) => setCases(r.data)).catch(() => {});
    api.get("/intakes").then((r) => setIntakes(r.data)).catch(() => {});
    api.get("/staff/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/meta/options").then((r) => setServiceTypes(r.data.service_types)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const createCase = async () => {
    if (!form.client_id || !form.service_type) {
      toast.error("Choose a client and a service type.");
      return;
    }
    try {
      await api.post("/cases", form);
      toast.success("Case created.");
      setCreateOpen(false);
      setForm({ client_id: "", service_type: "" });
      load();
    } catch {
      toast.error("Could not create the case.");
    }
  };

  const filteredCases = cases.filter((c) =>
    !query || c.client_name?.toLowerCase().includes(query.toLowerCase()) || c.service_type?.toLowerCase().includes(query.toLowerCase()) || c.status?.toLowerCase().includes(query.toLowerCase())
  );
  const filteredIntakes = intakes.filter((i) =>
    !query || i.contact?.name?.toLowerCase().includes(query.toLowerCase()) || (i.help_needed || []).join(" ").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="staff-cases-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight">Cases &amp; Leads</h1>
          <p className="mt-1 text-sm text-mutedink">Every client matter and incoming journey request.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full" data-testid="create-case-button"><Plus className="mr-2 h-4 w-4" /> New case</Button>
          </DialogTrigger>
          <DialogContent className="bg-surface">
            <DialogHeader><DialogTitle className="font-serif text-navy">Create a case</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="create-case-client"><SelectValue placeholder="Choose a client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name} — {c.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Service type</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="create-case-type"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createCase} className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="create-case-submit">Create case</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex gap-2">
          {[["cases", `Cases (${cases.length})`], ["intakes", `Intakes (${intakes.length})`]].map(([v, label]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`cases-tab-${v}`} className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${tab === v ? "bg-teal text-white" : "bg-surface border border-line text-mutedink hover:text-navy"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-mutedink" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." className="pl-9 rounded-full bg-surface" data-testid="cases-search" />
        </div>
      </div>

      {tab === "cases" && (
        <div className="bg-surface border border-line rounded-2xl shadow-sm overflow-hidden" data-testid="cases-table">
          {filteredCases.length === 0 ? (
            <p className="text-sm text-mutedink p-8 text-center">No cases yet. Cases are created automatically when a client claims their intake, or manually with "New case".</p>
          ) : (
            <div className="divide-y divide-line">
              {filteredCases.map((c) => (
                <Link key={c.case_id} to={`/staff/cases/${c.case_id}`} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-4 hover:bg-sand/40 transition-colors" data-testid={`case-row-${c.case_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-navy">{c.client_name}</p>
                    <p className="text-xs text-mutedink">{c.title}</p>
                  </div>
                  <span className="text-xs text-mutedink hidden sm:block">{new Date(c.updated_at).toLocaleDateString()}</span>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full w-fit ${STATUS_TONES[c.status] || "bg-teal-light text-teal"}`}>{c.status}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "intakes" && (
        <div className="bg-surface border border-line rounded-2xl shadow-sm overflow-hidden" data-testid="intakes-table">
          {filteredIntakes.length === 0 ? (
            <p className="text-sm text-mutedink p-8 text-center">No intakes yet.</p>
          ) : (
            <div className="divide-y divide-line">
              {filteredIntakes.map((i) => (
                <div key={i.intake_id} className="p-5" data-testid={`intake-detail-${i.intake_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-navy">{i.contact?.name || "Unnamed"} <span className="text-xs font-normal text-mutedink">· {i.contact?.email} · {i.contact?.phone}</span></p>
                      <p className="text-xs text-mutedink mt-0.5">{new Date(i.created_at).toLocaleString()} {i.contact?.city && `· ${i.contact.city}`} {i.contact?.language && `· Prefers ${i.contact.language}`}</p>
                    </div>
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-teal-light text-teal w-fit">{i.status}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(i.help_needed || []).map((h) => (
                      <span key={h} className="text-[10px] font-medium bg-sand px-2 py-1 rounded-full text-mutedink">{h}</span>
                    ))}
                  </div>
                  {Object.entries(i.urgent || {}).some(([, v]) => v) && (
                    <p className="mt-2 text-xs text-red-600 font-medium">
                      Urgent: {Object.entries(i.urgent).filter(([, v]) => v).map(([k]) => k.replace(/_/g, " ")).join(", ")}
                    </p>
                  )}
                  {i.details?.situation && <p className="mt-2 text-xs text-mutedink leading-relaxed border-l-2 border-line pl-3">{i.details.situation}</p>}
                  {i.appointment_pref?.service_type && (
                    <p className="mt-2 text-xs text-teal">Appointment preference: {i.appointment_pref.service_type} · {i.appointment_pref.mode} {i.appointment_pref.date && `· ${i.appointment_pref.date} ${i.appointment_pref.time || ""}`}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
